import { createContext, useContext, useCallback, useMemo, useState, type ReactNode } from 'react';
import { useLatest } from '@/client/hooks/use-latest';
import type { WizardContextValue, WizardStep } from './types';

const WizardContext = createContext<WizardContextValue | null>(null);

interface WizardProviderProps {
  children: ReactNode;
  steps: WizardStep[];
  initialStep?: number;
  isEditMode?: boolean;
  /** Validation function called before proceeding to next step */
  validateStep?: (stepIndex: number) => Promise<boolean> | boolean;
}

export function WizardProvider({
  children,
  steps,
  initialStep = 0,
  isEditMode = false,
  validateStep,
}: WizardProviderProps) {
  const [currentStep, setCurrentStep] = useState(initialStep);
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(
    // In edit mode, mark all steps as initially completed
    isEditMode ? new Set(steps.map((_, i) => i)) : new Set()
  );
  const [visitedSteps, setVisitedSteps] = useState<Set<number>>(new Set([initialStep]));
  const [isSubmitting, setSubmitting] = useState(false);
  const [errors, setErrorsState] = useState<Record<string, string[]>>({});

  const totalSteps = steps.length;

  // ⚡ Performance: Keep stable reference to validateStep while always calling latest version
  const validateStepRef = useLatest(validateStep);

  // Navigation helpers
  const canNavigateTo = useCallback(
    (index: number) => {
      // Can always go back to visited steps
      if (visitedSteps.has(index)) return true;
      // Can go to next step if current is complete
      if (index === currentStep + 1 && completedSteps.has(currentStep)) return true;
      // In edit mode, can navigate anywhere
      if (isEditMode) return true;
      return false;
    },
    [visitedSteps, currentStep, completedSteps, isEditMode]
  );

  const goToStep = useCallback(
    (index: number) => {
      if (index < 0 || index >= totalSteps) return;
      if (!canNavigateTo(index) && !isEditMode) return;

      setCurrentStep(index);
      setVisitedSteps((prev) => new Set([...prev, index]));
    },
    [totalSteps, canNavigateTo, isEditMode]
  );

  const nextStep = useCallback(async () => {
    if (currentStep >= totalSteps - 1) return false;

    // Run validation if provided (use ref to avoid dependency)
    if (validateStepRef.current) {
      const isValid = await validateStepRef.current(currentStep);
      if (!isValid) return false;
    }

    // Mark current step as complete
    setCompletedSteps((prev) => new Set([...prev, currentStep]));

    // Move to next step
    const nextIndex = currentStep + 1;
    setCurrentStep(nextIndex);
    setVisitedSteps((prev) => new Set([...prev, nextIndex]));

    return true;
  }, [currentStep, totalSteps, validateStepRef]);

  const prevStep = useCallback(() => {
    if (currentStep <= 0) return;
    setCurrentStep(currentStep - 1);
  }, [currentStep]);

  const markStepComplete = useCallback((index: number) => {
    setCompletedSteps((prev) => new Set([...prev, index]));
  }, []);

  const markStepIncomplete = useCallback((index: number) => {
    setCompletedSteps((prev) => {
      const next = new Set(prev);
      next.delete(index);
      return next;
    });
  }, []);

  const setErrors = useCallback((stepId: string, stepErrors: string[]) => {
    setErrorsState((prev) => ({ ...prev, [stepId]: stepErrors }));
  }, []);

  const clearErrors = useCallback((stepId: string) => {
    setErrorsState((prev) => {
      const next = { ...prev };
      delete next[stepId];
      return next;
    });
  }, []);

  const value = useMemo<WizardContextValue>(() => {
    // Ensure we have a valid step config (fallback to first step if out of bounds)
    const safeIndex = Math.max(0, Math.min(currentStep, steps.length - 1));
    const currentStepConfig = steps[safeIndex]!; // Safe: steps array is guaranteed non-empty by component contract
    const isFirstStep = currentStep === 0;
    const isLastStep = currentStep === totalSteps - 1;

    // Can proceed if current step is optional or has been completed
    const canProceed = currentStepConfig.optional || completedSteps.has(currentStep);

    return {
      // State
      currentStep,
      totalSteps,
      completedSteps,
      visitedSteps,
      isSubmitting,
      isEditMode,
      errors,
      // Derived
      steps,
      currentStepConfig,
      canNavigateTo,
      canProceed,
      isFirstStep,
      isLastStep,
      // Actions
      goToStep,
      nextStep,
      prevStep,
      markStepComplete,
      markStepIncomplete,
      setErrors,
      clearErrors,
      setSubmitting,
    };
  }, [
    currentStep,
    totalSteps,
    completedSteps,
    visitedSteps,
    isSubmitting,
    isEditMode,
    errors,
    steps,
    canNavigateTo,
    goToStep,
    nextStep,
    prevStep,
    markStepComplete,
    markStepIncomplete,
    setErrors,
    clearErrors,
  ]);

  return <WizardContext.Provider value={value}>{children}</WizardContext.Provider>;
}

export function useWizard(): WizardContextValue {
  const context = useContext(WizardContext);
  if (!context) {
    throw new Error('useWizard must be used within a WizardProvider');
  }
  return context;
}

export { WizardContext };
